import React, { FC, PropsWithChildren, ReactNode, useRef, useState } from "react";
import ReactDOM from "react-dom";

import "./Tooltip.scss";

export interface StaticTooltipHtmlProps extends PropsWithChildren {
  content: ReactNode | string;
  position?: "top" | "top-end" | "bottom" | "bottom-end" | "left" | "bottom-right";
}

export const StaticTooltipHtml: FC<StaticTooltipHtmlProps> = ({ content, children, position = "bottom" }) => {
  const [tooltipPortal, setTooltipPortal] = useState<React.ReactNode>();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseLeave = () => {
    clearTimeout(hideTimeoutRef.current!);

    hideTimeoutRef.current = setTimeout(() => {
      setTooltipPortal(null);
    }, 100);
  };

  const handleMouseEnter = (evt: React.MouseEvent) => {
    clearTimeout(hideTimeoutRef.current!);
    const { x, y, height, width } = evt.currentTarget.getBoundingClientRect();

    hideTimeoutRef.current = setTimeout(() => {
      let top, left;
      let transform = "";

      switch (position) {
        case "top":
          top = y - height - 10 + window.scrollY - (tooltipRef.current ? tooltipRef.current.offsetHeight / 2 : 0);
          left = x + width / 2;
          transform = "translateX(-50%)";
          break;
        case "top-end":
          top = y - height * 2 - 20 - (tooltipRef.current ? tooltipRef.current.offsetHeight / 2 : 0);
          left = x + width;
          transform = "translateX(-100%)";
          break;
        case "bottom":
          top = y + height + 10 + window.scrollY;
          left = x + width / 2;
          transform = "translateX(-50%)";
          break;
        case "bottom-end":
          top = y + height + 10 + window.scrollY;
          left = x + width;
          transform = "translateX(-100%)";
          break;
        case "bottom-right":
          top = y + height + 10 + window.scrollY;
          left = x;
          transform = "";
          break;
        case "left":
          top = y - 10 + window.scrollY;
          left = x - 10;
          transform = "translateX(-100%)";
          break;
      }

      const style = { top: `${top}px`, left: `${left}px`, transform };

      if (content !== null) {
        setTooltipPortal(
          <div
            className="tooltip-content"
            style={style}
            onMouseEnter={() => clearTimeout(hideTimeoutRef.current!)}
            onMouseLeave={handleMouseLeave}
          >
            {typeof content === "string" ? <div dangerouslySetInnerHTML={{ __html: content }} /> : content}
          </div>,
        );
      }
    }, 100);
  };

  return (
    <>
      <div className="tooltip" ref={tooltipRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
      {tooltipPortal && ReactDOM.createPortal(tooltipPortal, document.body)}
    </>
  );
};
